'use client';

import { c as _c } from "react/compiler-runtime";
import { usePrefetchGetMarkets } from '@morfi/backend-sdk-v2/client';
import { m, useReducedMotion } from 'motion/react';
import { useTranslations } from 'next-intl';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { CommandIcon, Search02Icon } from '@/components/ui/icon/glyphs';
import { TRENDING_SEARCH_STATUS } from '@/lib/sdk/projections/search';
import { MOTION_TAP } from '@/lib/ui/easing';
import { cn } from '@/lib/ui/utils';
import { useLayoutStore } from '@/stores/layout';
const PILL_TAP = {
  y: 0,
  scale: 0.97,
  transition: MOTION_TAP
} as const;

/**
 * Search control for the markets heading row.
 *
 * - Desktop/tablet (`sm+`): pill-shaped button styled like a search input —
 *   elevated surface (`bg-background-secondary`; `dark:bg-background-primary`),
 *   thin border, search icon + markets `searchPlaceholder` label.
 * - Mobile (below `sm`): AnimatedButton icon-only matching the toolbar style.
 *
 * Both variants open the global command search modal on click.
 */
export function SearchInputButton() {
  const $ = _c(30);
  const tHeader = useTranslations("header");
  const tMarkets = useTranslations("markets");
  const setSearchOpen = useLayoutStore(_temp);
  const prefersReducedMotion = useReducedMotion();
  const prefetchMarkets = usePrefetchGetMarkets();
  let t0;
  if ($[0] !== setSearchOpen) {
    t0 = function handleClick() {
      setSearchOpen(true);
    };
    $[0] = setSearchOpen;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const handleClick = t0;
  let t1;
  if ($[2] !== prefetchMarkets) {
    t1 = function handlePointerEnter() {
      prefetchMarkets({
        sortBy: "createdAt",
        sortOrder: "desc",
        status: TRENDING_SEARCH_STATUS
      });
    };
    $[2] = prefetchMarkets;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handlePointerEnter = t1;
  let t2;
  if ($[4] !== tHeader) {
    t2 = tHeader("search");
    $[4] = tHeader;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== handleClick || $[7] !== t2) {
    t3 = <AnimatedButton variant="tertiary" size="sm" prefix={Search02Icon} aria-label={t2} onClick={handleClick} className="sm:hidden" />;
    $[6] = handleClick;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== prefersReducedMotion) {
    t4 = prefersReducedMotion ? {} : {
      whileTap: PILL_TAP
    };
    $[9] = prefersReducedMotion;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = cn("inline-flex max-sm:hidden h-8 w-48 min-w-0 items-center gap-2 rounded-full pl-2.5 pr-2", "border border-border-primary shadow-control-crisp", "bg-background-primary dark:bg-background-primary", "text-foreground-tertiary text-body transition-colors", "hover:border-border-primary hover:bg-background-primary hover:text-foreground-secondary");
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== tHeader) {
    t6 = tHeader("search");
    $[12] = tHeader;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <Icon icon={Search02Icon} className="size-3.5 shrink-0" />;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== tMarkets) {
    t8 = tMarkets("searchPlaceholder");
    $[15] = tMarkets;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t8) {
    t9 = <span className="min-w-0 flex-1 truncate text-left">{t8}</span>;
    $[17] = t8;
    $[18] = t9;
  } else {
    t9 = $[18];
  }
  let t10;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <kbd className="inline-flex size-5 items-center justify-center rounded-full bg-background-tertiary text-caption font-medium text-foreground-tertiary">K</kbd>;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span aria-hidden={true} className="flex items-center gap-1 shrink-0">{t10}<kbd className="inline-flex size-5 items-center justify-center rounded-full bg-background-tertiary text-foreground-tertiary"><Icon icon={CommandIcon} className="size-3" /></kbd></span>;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== handleClick || $[22] !== handlePointerEnter || $[23] !== t4 || $[24] !== t6 || $[25] !== t9) {
    t12 = <m.button type="button" {...t4} transition={MOTION_TAP} className={t5} aria-label={t6} onClick={handleClick} onPointerEnter={handlePointerEnter}>{t7}{t9}{t11}</m.button>;
    $[21] = handleClick;
    $[22] = handlePointerEnter;
    $[23] = t4;
    $[24] = t6;
    $[25] = t9;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== t3) {
    t13 = <>{t3}{t12}</>;
    $[27] = t12;
    $[28] = t3;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
}
function _temp(s) {
  return s.setSearchOpen;
}